import { Key, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Paging,
  Table,
  usePaging,
  useAsync,
  Form,
  Input,
  CustomFilter,
  Select,
  EllipsisSpan,
  Modal,
  MultiSelect,
} from '@maxtropy/components';
import { DefaultOptionType } from 'antd/es/cascader';
import { useQuery } from '../../../../utils/utils';
import styles from './index.module.scss';
import {
  apiV2ComprehensiveLabelBindPost,
  apiV2RuleDeviceBindPost,
  apiV2RuleDeviceGetBindRuleListPost,
  apiV2RuleGroupDeviceAllRuleGroupPost,
  apiV2RuleRulePagePost,
} from '@maxtropy/device-customer-apis-v2';

interface FilterParams {
  name?: string;
  tag?: string;
  typeId: Array<Array<number>>;
}

interface TableDataType {}

const columns = (isDetail?: boolean) => {
  const edgeDeviceId = isDetail
    ? [
        {
          title: '物理设备ID',
          dataIndex: 'id',
          ellipsis: { showTitle: true },
          render: (v: string) => <EllipsisSpan value={v} />,
        },
      ]
    : [];
  return [
    {
      title: '规则名称',
      dataIndex: 'name',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '规则状态',
      dataIndex: 'status',
      ellipsis: { showTitle: true },
      render: (v: number) => <EllipsisSpan value={`${v ? '禁用' : '启用'}`} />,
    },
    ...edgeDeviceId,
    {
      title: '规则编号',
      dataIndex: 'code',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '所属规则组',
      dataIndex: 'ruleGroupName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
  ];
};
interface Iprops {
  linkIds?: number[];
  labelId?: number;
  onChangeSelectKeys?: (keys?: number[]) => void;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const AssociateLoopsModal = ({ linkIds, labelId, onChangeSelectKeys, onConfirm, onCancel }: Iprops) => {
  const pagingInfo = usePaging();
  const [form] = Form.useForm();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<any>();
  const [selectedRows, setSelectedRows] = useState<any[]>();
  const [selectedItems, setSelectItems] = useState<any[]>((linkIds || []).map(id => ({ id })));
  const [nameOptions, setNameOptions] = useState<any[]>([]);
  const { id } = useParams<{ id: string }>();
  //console.log('selectedItems :>> ', selectedItems);
  const { data, isLoading }: { data?: any[]; isLoading?: boolean } = useQuery(
    useCallback(async () => {
      const res = await apiV2RuleRulePagePost({
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      });
      if (res) setTotalCount(res.total as number);
      return res.list;
    }, [id, pageOffset, pageSize, searchParams, setTotalCount])
  );

  const onReset = () => {
    form.resetFields();
    form.submit();
    setPageOffset(1);
  };
  useEffect(() => {
    apiV2RuleGroupDeviceAllRuleGroupPost({}).then(res => {
      res.list && setNameOptions(res.list);
    });
  }, []);
  const onFinish = (val: any) => {
    // const deviceTypes = getAllChild(val.typeId, formatOptionData(deviceTypeData));
    const params: any = {
      codeOrNameLike: val.name,
      ruleGroupIdList: val.ruleGroupIdList,
    };
    setSearchParams(params);
    setPageOffset(1);
  };

  const onSelectChange = (rowKeys: any[], rows: any[]) => {
    // 获取所有当前应该选中的ID
    const currentSelectedIds = new Set(rowKeys);

    // 从selectedItems中过滤出仍然被选中的项
    const remainingSelectedItems = selectedItems?.filter(item => currentSelectedIds.has(item.id)) || [];

    // 获取新增选中的项(在rows中但不在selectedItems中)
    const newSelectedRows = rows.filter(item => !!item && !selectedItems?.map(s => s.id).includes(item.id));

    setSelectItems(remainingSelectedItems);
    setSelectedRows(newSelectedRows);
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys: (selectedRows || []).concat(selectedItems || []).map(item => item.id as Key),
    onChange: onSelectChange,
  };

  const length = (selectedItems?.length || 0) + (selectedRows?.length || 0);

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);
  return (
    <Modal
      size="big"
      title="关联规则"
      open
      destroyOnClose
      onOk={() => {
        const selectedIds = (selectedRows || []).concat(selectedItems || []).map(item => item.id);
        apiV2ComprehensiveLabelBindPost({
          type: 2,
          labelId,
          ids: selectedIds,
        }).then(() => {
          onConfirm?.();
        });
      }}
      onCancel={() => {
        onCancel?.();
      }}
    >
      <div className={styles.deviceModal}>
        <CustomFilter<FilterParams> form={form} onFinish={val => onFinish(val as FilterParams)} onReset={onReset}>
          <Form.Item name="name" label="规则编号/名称">
            <Input placeholder={'请输入编号或名称查询'} />
          </Form.Item>
          <Form.Item label="规则组名称" name="ruleGroupIdList">
            <MultiSelect
              placeholder="请选择"
              showSearch
              allowClear
              options={nameOptions.map(i => ({ label: i.name, value: i.id }))}
            ></MultiSelect>
          </Form.Item>
        </CustomFilter>

        <Table<TableDataType>
          scroll={{ y: 300 }}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          columns={columns()}
          dataSource={data}
          bottomLeftExtra={
            <div className={styles.tips}>
              <span>本次新增勾选</span>
              <span className={styles.red}>{selectedRows?.length || '0'}</span>
              <span>项，</span>
              <span>总计</span>
              <span className={styles.red}>{length}</span>
              <span>项</span>
            </div>
          }
        >
          <Paging pagingInfo={pagingInfo} />
        </Table>
      </div>
    </Modal>
  );
};

export default AssociateLoopsModal;
